import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, Save, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { api, IDR, formatErr, NUM } from "@/lib/api";
import { canApprove, useAuth } from "@/lib/auth";
import DataTable, { EmptyState } from "@/components/app/DataTable";

export default function Pricing() {
  const { user } = useAuth();
  const [sp] = useSearchParams();
  const preselect = sp.get("hpp");
  const [hppList, setHppList] = useState([]);
  const [selected, setSelected] = useState(preselect || "");
  const [method, setMethod] = useState("markup");
  const [percent, setPercent] = useState(25);
  const [extra, setExtra] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [tax, setTax] = useState(0);
  const [rounding, setRounding] = useState(100);
  const [market, setMarket] = useState("");
  const [notes, setNotes] = useState("");
  const [result, setResult] = useState(null);
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [{ data: hs }, { data: scs }] = await Promise.all([api.get("/hpp"), api.get("/selling-price/scenarios")]);
      setHppList(hs); setScenarios(scs);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const selectedHpp = useMemo(() => hppList.find((h) => h.id === selected), [selected, hppList]);

  const simulate = async (save = false) => {
    if (!selected) { toast.error("Pilih perhitungan HPP terlebih dahulu"); return; }
    setSaving(save);
    try {
      const { data } = await api.post("/selling-price/simulate", {
        hpp_calc_id: selected, method, percent: Number(percent) / 100,
        extra_cost_per_unit: Number(extra || 0),
        discount_percent: Number(discount || 0) / 100,
        tax_percent: Number(tax || 0) / 100,
        rounding: Number(rounding || 0),
        market_price: market ? Number(market) : null,
        save, notes,
      });
      setResult(data);
      if (save) { toast.success("Skenario tersimpan"); load(); }
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };

  useEffect(() => { if (selected) simulate(false); /* eslint-disable-next-line */ }, [selected, method, percent, extra, discount, tax, rounding]);

  if (loading) return <div className="gc-card"><Loader2 className="w-4 h-4 animate-spin" /></div>;

  return (
    <div className="space-y-6">
      <div>
        <span className="gc-label">Analisis</span>
        <h1 className="gc-h1 mt-1">Simulasi Harga Jual</h1>
        <p className="mt-2 text-slate-500 max-w-2xl">Tentukan harga jual berbasis HPP dengan metode markup atau margin. Simpan skenario untuk dibandingkan.</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="gc-card lg:col-span-2 space-y-4">
          <div>
            <label className="gc-label block mb-1.5">Perhitungan HPP *</label>
            <select className="gc-input" value={selected} onChange={(e) => setSelected(e.target.value)} data-testid="sim-hpp-select">
              <option value="">— Pilih —</option>
              {hppList.map((h) => <option key={h.id} value={h.id}>{h.calc_no} — {IDR(h.hpp_per_unit)}/unit ({h.kind})</option>)}
            </select>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="gc-label block mb-1.5">Metode</label>
              <div className="flex rounded-xl border border-slate-200 p-1 bg-slate-50">
                {["markup", "margin"].map((m) => (
                  <button key={m} onClick={() => setMethod(m)} type="button" className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-colors ${method === m ? "bg-white text-emerald-700 shadow-sm" : "text-slate-500"}`} data-testid={`btn-method-${m}`}>{m === "markup" ? "Markup" : "Margin"}</button>
                ))}
              </div>
              <p className="text-[11px] text-slate-400 mt-1">
                {method === "markup" ? "Harga = HPP × (1 + markup%)" : "Harga = HPP ÷ (1 − margin%)"}
              </p>
            </div>
            <div>
              <label className="gc-label block mb-1.5">{method === "markup" ? "Markup %" : "Margin %"}</label>
              <input type="number" step="0.5" min={0} className="gc-input" value={percent} onChange={(e) => setPercent(e.target.value)} data-testid="sim-percent" />
            </div>
            <div>
              <label className="gc-label block mb-1.5">Biaya Nonproduksi / Unit (IDR)</label>
              <input type="number" className="gc-input" value={extra} onChange={(e) => setExtra(e.target.value)} />
            </div>
            <div>
              <label className="gc-label block mb-1.5">Diskon %</label>
              <input type="number" className="gc-input" value={discount} onChange={(e) => setDiscount(e.target.value)} />
            </div>
            <div>
              <label className="gc-label block mb-1.5">Pajak %</label>
              <input type="number" className="gc-input" value={tax} onChange={(e) => setTax(e.target.value)} />
            </div>
            <div>
              <label className="gc-label block mb-1.5">Pembulatan (Rp)</label>
              <input type="number" className="gc-input" value={rounding} onChange={(e) => setRounding(e.target.value)} />
            </div>
            <div>
              <label className="gc-label block mb-1.5">Harga Pasar (opsional)</label>
              <input type="number" className="gc-input" value={market} onChange={(e) => setMarket(e.target.value)} placeholder="Untuk perbandingan" />
            </div>
            <div>
              <label className="gc-label block mb-1.5">Catatan</label>
              <input className="gc-input" value={notes} onChange={(e) => setNotes(e.target.value)} />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
            <button onClick={() => simulate(false)} className="gc-btn-ghost" data-testid="btn-preview"><TrendingUp className="w-4 h-4" /> Preview</button>
            <button onClick={() => simulate(true)} disabled={saving || !selected} className="gc-btn-primary" data-testid="btn-save-scenario">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><Save className="w-4 h-4" /> Simpan Skenario</>)}</button>
          </div>
        </div>

        <div className="space-y-4">
          <div className="rounded-2xl bg-slate-900 text-white p-6">
            <div className="gc-label !text-emerald-300">Hasil Simulasi</div>
            {result ? (
              <div className="mt-3 space-y-3">
                <div>
                  <div className="text-xs text-slate-300">HPP Efektif / Unit</div>
                  <div className="font-cabinet text-2xl font-black">{IDR(result.hpp_per_unit)}</div>
                </div>
                <div className="border-t border-white/10 pt-3">
                  <div className="text-xs text-slate-300">Harga Jual Rekomendasi</div>
                  <div className="font-cabinet text-4xl font-black text-emerald-300" data-testid="sim-price">{IDR(result.price_per_unit)}</div>
                </div>
                <div className="text-xs space-y-1 border-t border-white/10 pt-3">
                  <div className="flex justify-between"><span>Laba / Unit</span><span className="font-semibold">{IDR(result.profit_per_unit)}</span></div>
                  <div className="flex justify-between"><span>Laba / Batch</span><span className="font-semibold">{IDR(result.profit_per_batch)}</span></div>
                  <div className="flex justify-between"><span>Markup Efektif</span><span>{NUM(result.effective_markup_percent)}%</span></div>
                  <div className="flex justify-between"><span>Margin Efektif</span><span>{NUM(result.effective_margin_percent)}%</span></div>
                  {market && Number(market) > 0 && (
                    <div className="flex justify-between text-amber-300 border-t border-white/10 mt-2 pt-2"><span>Harga Pasar</span><span>{IDR(market)}</span></div>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-sm text-slate-400 mt-3">Pilih HPP dan atur parameter untuk melihat harga rekomendasi.</div>
            )}
          </div>
          {selectedHpp && (
            <div className="gc-card text-xs space-y-1.5">
              <div className="gc-label">HPP Terpilih</div>
              <div>No: <span className="font-semibold">{selectedHpp.calc_no}</span></div>
              <div>HPP/Unit: <span className="font-semibold">{IDR(selectedHpp.hpp_per_unit)}</span></div>
              <div>Total Biaya: <span className="font-semibold">{IDR(selectedHpp.total_cost)}</span></div>
              <div>Qty Baik: <span className="font-semibold">{NUM(selectedHpp.good_qty, 0)}</span></div>
            </div>
          )}
        </div>
      </div>

      <div>
        <h3 className="gc-h3 mb-3">Skenario Tersimpan</h3>
        <DataTable
          rows={scenarios}
          columns={[
            { key: "scenario_no", header: "No.", render: (r) => <span className="font-semibold">{r.scenario_no}</span> },
            { key: "method", header: "Metode", render: (r) => r.method === "markup" ? "Markup" : "Margin" },
            { key: "percent", header: "%", align: "right", render: (r) => `${NUM((r.percent || 0) * 100)}%` },
            { key: "hpp_per_unit", header: "HPP/Unit", align: "right", render: (r) => IDR(r.hpp_per_unit) },
            { key: "price_per_unit", header: "Harga Jual", align: "right", render: (r) => <span className="font-semibold text-emerald-700">{IDR(r.price_per_unit)}</span> },
            { key: "profit_per_unit", header: "Laba/Unit", align: "right", render: (r) => IDR(r.profit_per_unit) },
            { key: "notes", header: "Catatan" },
          ]}
          empty={<EmptyState title="Belum ada skenario" desc="Simpan skenario simulasi untuk analisis perbandingan." />}
        />
      </div>
    </div>
  );
}
